import { useState, useEffect, useMemo } from "react";
import { Table } from "react-bootstrap";
import { FaCircle } from "react-icons/fa6";
import { IoMdCloseCircleOutline } from "react-icons/io";
import { RiCollapseDiagonal2Fill } from "react-icons/ri";
import { FaDiamond } from "react-icons/fa6";
import { FaCheckCircle, FaHubspot, FaExpandAlt } from "react-icons/fa";
import { MdOutlineOndemandVideo } from "react-icons/md";
import TranscriptExpander from "./TranscriptExpander";
import type { TranscriptTABLEProps } from "./types";
import { getSentimentColor } from "./utils";
import { css } from "../../../styled-system/css";
import { ButtonGroup } from "components/core/basic/ButtonGroup";

const extractQueryFromHash = (queryString: string, hash: string): string => {
  const hashParams = new URLSearchParams(hash.substring(1));
  return hashParams.get(queryString) || "";
};

const TranscriptTable = ({ transcripts }: TranscriptTABLEProps) => {
  const [hash, setHash] = useState<string>(extractQueryFromHash("id", window.location.hash));
  const [timeValue, setTimeValue] = useState<string>(extractQueryFromHash("time", window.location.hash));
  const [expanded, setExpanded] = useState<boolean>(!!timeValue);
  const [meetingType, setMeetingType] = useState<string>("all");
  const PAGE_SIZE = 20;
  const [page, setPage] = useState(1);
  const rows = useMemo(() => transcripts[0]?.channel_response ?? [], [transcripts]);
  const totalPages = Math.ceil(rows.length / PAGE_SIZE);
  const relationId = extractQueryFromHash("relation", window.location.hash);

  const updateHash = (queryVar: string, newHash: string): void => {
    let hash = `${queryVar}=${newHash}`;
    const relationId = extractQueryFromHash("relation", window.location.hash);
    if (relationId && queryVar !== "relation") {
      hash += `&relation=${relationId}`;
    }
    window.location.hash = hash;
  };

  const closeExpanded = (): void => {
    setHash("");
    setExpanded(false);
    const relationId = extractQueryFromHash("relation", window.location.hash);
    if (relationId) {
      window.location.hash = `relation=${relationId}`;
    } else {
      window.location.hash = "";
    }
  };

  const selectedRow = useMemo(() => {
    return rows.find((row) => row.file_id === hash);
  }, [hash, rows]);

  const pagedRows = useMemo(() => {
    // Filter meeting type if not "all"
    const filteredRows = meetingType === "all" ? rows : rows.filter((row) => row.call_type === meetingType);
    const totalFiltered = filteredRows.length;
    if (totalFiltered === 0) {
      return [];
    }
    const totalFilteredPages = Math.ceil(totalFiltered / PAGE_SIZE);
    if (page > totalFilteredPages) {
      setPage(totalFilteredPages);
    }
    // Return the paginated rows based on the current page
    return filteredRows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  }, [page, rows, meetingType]);

  const countOfAllMeetingTypes = useMemo(() => {
    const counts = rows.reduce(
      (acc, row) => {
        acc[row.call_type] = (acc[row.call_type] || 0) + 1;
        acc["all"] = (acc["all"] || 0) + 1;
        return acc;
      },
      {} as Record<string, number>,
    );
    return counts;
  }, [rows]);

  // const countOfAttendees = useMemo(() => {
  //   const counts: Record<string, number> = {};
  //   rows.forEach((row) => {
  //     row.attendees.forEach((attendee) => {
  //       // If attendee.company_name exists and contains "trellis" (case-insensitive)
  //       if (attendee.company && attendee.company.toLowerCase().includes("trellis")) {
  //         counts[attendee.name] = (counts[attendee.name] || 0) + 1;
  //       }
  //     });
  //   });
  //   return counts;
  // }, [rows]);

  useEffect(() => {
    const onHashChange = (): void => {
      const fileId = extractQueryFromHash("id", window.location.hash);
      const timeValue = extractQueryFromHash("time", window.location.hash);
      if (fileId) {
        setHash(fileId);
        if (timeValue) {
          setTimeValue(timeValue);
          setExpanded(true);
        } else {
          setTimeValue("");
        }
      } else {
        setHash("");
      }
    };

    window.addEventListener("hashchange", onHashChange);

    return () => {
      window.removeEventListener("hashchange", onHashChange);
    };
  }, []);

  const CALL_TYPES: { [key: string]: string } = {
    CUSTOMER_SUCCESS: "Customer Success",
    SALES: "Sales",
    INTERNAL: "Internal",
    OTHER: "Other",
    ONBOARDING: "Onboarding",
    Unknown: "Unknown",
  };

  return (
    <div className={css({ display: "flex", gap: "2rem" })}>
      {!expanded && (
        <div className={css({ flex: 1, width: !hash ? "100%" : "50%", marginTop: "2rem" })}>
          <div className={css({ display: "flex", justifyContent: "space-between", alignItems: "center" })}>
            <div className={css({ flex: 0.5, marginBottom: "1.5rem" })}>
              <ButtonGroup
                value={meetingType}
                onSelect={setMeetingType}
                buttons={[
                  {
                    label: "All",
                    value: "all",
                    counter: countOfAllMeetingTypes["all"] || 0,
                  },
                  {
                    label: "CS",
                    value: "CUSTOMER_SUCCESS",
                    counter: countOfAllMeetingTypes["CUSTOMER_SUCCESS"] || 0,
                  },
                  {
                    label: "Onboarding",
                    value: "ONBOARDING",
                    counter: countOfAllMeetingTypes["ONBOARDING"] || 0,
                  },
                  {
                    label: "Sales",
                    value: "SALES",
                    counter: countOfAllMeetingTypes["SALES"] || 0,
                  },
                  {
                    label: "Other",
                    value: "OTHER",
                    counter: countOfAllMeetingTypes["OTHER"] || 0,
                  },
                ]}
              />
            </div>
            <div className={css({ flex: 0.5 })}>
              {totalPages > 1 && (
                <div
                  className={css({
                    display: "flex",
                    justifyContent: "right",
                    alignItems: "center",
                    marginBottom: "3rem",
                  })}
                >
                  <button
                    className={css({ border: "1px solid #ccc", marginRight: "0.5rem" })}
                    disabled={page === 1}
                    onClick={() => setPage(page - 1)}
                  >
                    Previous
                  </button>
                  <span>
                    Page {page} of {totalPages}
                  </span>
                  <button
                    className={css({ border: "1px solid #ccc", marginLeft: "0.5rem" })}
                    disabled={page === totalPages}
                    onClick={() => setPage(page + 1)}
                  >
                    Next
                  </button>
                </div>
              )}
              {relationId && (
                <div
                  className={css({
                    display: "flex",
                    justifyContent: "right",
                    alignItems: "center",
                    marginBottom: "3rem",
                  })}
                >
                  <button
                    className={css({ border: "1px solid #ccc", marginRight: "0.5rem" })}
                    onClick={() => {
                      window.location.hash = ``;
                    }}
                  >
                    Clear
                  </button>
                </div>
              )}
              {totalPages <= 1 && !relationId && <div className={css({ height: "4rem" })} />}
            </div>
          </div>
          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th className={`text-start`}>Title</th>
                <th className={`text-start`}>Meeting Date</th>
                <th className={`text-start`}>Meeting Type</th>
                {!hash && <th className={`text-start`}>Attendees</th>}
              </tr>
            </thead>
            <tbody>
              {pagedRows.map((row, idx) => (
                <tr key={idx + (page - 1) * PAGE_SIZE}>
                  <td>
                    <div className={`my-2`}>
                      <FaCircle
                        className={css({ marginRight: "8px" })}
                        style={{ color: getSentimentColor(row.sentiment) }}
                        title={row.sentiment}
                      />

                      {row.companies && row.companies.length > 0 && (
                        <>
                          <a
                            href={`https://app.hubspot.com/contacts/6658395/company/${row.companies[0]?.relation_id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <FaHubspot className={css({ marginRight: "8px", color: "#ff7f50" })} />
                          </a>
                          <span
                            className={css({ cursor: "pointer", fontSize: "1rem" })}
                            onClick={() => {
                              if (row.companies && row.companies[0]?.relation_id) {
                                updateHash("relation", row.companies[0].relation_id);
                              }
                            }}
                          >
                            <FaDiamond className={css({ marginRight: "8px", color: "trellisPalette.0" })} />
                          </span>
                        </>
                      )}
                      {row.recording_share_url && (
                        <a href={row.recording_share_url} target="_blank" rel="noopener noreferrer">
                          <MdOutlineOndemandVideo className={css({ marginRight: "8px", color: "#E50914" })} />
                        </a>
                      )}
                      <span className={css({ cursor: "pointer" })} onClick={() => updateHash("id", row.file_id)}>
                        {row.title}
                        {row.companies && row.companies.length > 0 && (
                          <span className={css({ marginLeft: "0.5rem" })}>
                            <small>
                              <em>({row.companies[0]?.company_name})</em>
                            </small>
                          </span>
                        )}
                      </span>

                      {hash === row.file_id && (
                        <FaCheckCircle className={css({ marginLeft: "8px", color: "#007bff" })} />
                      )}
                    </div>
                  </td>
                  <td>{row.meeting_date}</td>
                  <td>{CALL_TYPES[row.call_type]}</td>
                  {!hash && <td>{row.attendees.map((attendee) => attendee.name).join(", ")}</td>}
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}
      {hash && selectedRow && (
        <div className={css({ flex: 1, width: !hash ? "100%" : "50%", marginTop: "4rem" })}>
          <div
            className={css({
              border: "0px solid #ccc",
              borderRadius: "8px",
              minHeight: "100%",
              marginLeft: "0.5rem",
              marginRight: "0.5rem",
              backgroundColor: "#fff",
            })}
          >
            <div
              className={css({
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "1rem",
                padding: "0rem 0rem",
              })}
            >
              <span className={css({ cursor: "pointer", fontSize: "1.5rem" })} onClick={() => setExpanded(!expanded)}>
                {expanded ?
                  <RiCollapseDiagonal2Fill />
                : <FaExpandAlt />}
              </span>
              <span className={css({ fontSize: "1.5rem", fontWeight: "bold" })}>
                {selectedRow.title}{" "}
                {selectedRow.recording_share_url && (
                  <a href={selectedRow.recording_share_url} target="_blank" rel="noopener noreferrer">
                    <MdOutlineOndemandVideo className={css({ marginRight: "8px", color: "#E50914" })} />
                  </a>
                )}
              </span>
              <IoMdCloseCircleOutline
                className={css({ cursor: "pointer", fontSize: "2rem" })}
                onClick={() => {
                  closeExpanded();
                }}
              />
            </div>
            <div className={css({ padding: "1rem 0rem" })}>
              <TranscriptExpander file_id={hash} expanded={expanded} timeValue={timeValue} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TranscriptTable;
